import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { login } from '../services/api';

function LoginPage({ onLogin }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const data = await login(username, password);
      if (data.success) {
        sessionStorage.setItem('mts-user', JSON.stringify(data.user));
        onLogin(data.user);
      }
    } catch (err) {
      setError(err.message || t('login.invalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  const footerLinkSx = {
    color: isDark ? '#ffffff' : 'rgba(0,0,0,0.87)',
    cursor: 'pointer',
    fontWeight: 500,
    textDecoration: 'none',
    '&:hover': { color: isDark ? '#a3a3a3' : 'rgba(0,0,0,0.6)' },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        pb: '56px',
      }}
    >
      <Card sx={{ width: 400, maxWidth: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              component="img"
              // src={isDark ? '/white_forge_PNG.png' : '/black_forge_PNG.png'}
              src='/Forge_Black.png'
              alt="MTS Forge"
              sx={{ height: 80, width: 'auto', mx: 'auto', mb: 2, display: 'block', filter: isDark ? 'invert(1)' : 'none' }}
            />
            <Typography variant="h5" fontWeight={700}>
              {t('login.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Menard Technical Services
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={t('login.email')}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
              autoFocus
            />
            <TextField
              fullWidth
              label={t('login.password')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 3 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              sx={{
                py: 1.2,
                color: '#FFFFFF',
                backgroundColor: 'secondary.main',
                '&:hover': { backgroundColor: 'secondary.dark' },
              }}
            >
              {loading ? t('login.signingIn') : t('login.signIn')}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Footer — same bar as the logged-in layout, minus the version number */}
      <Box
        component="footer"
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: 56,
          backgroundColor: isDark ? '#0a0a0a' : '#fafafa',
          borderTop: isDark ? '1px solid #1a1a1a' : 'none',
          boxShadow: isDark ? 'none' : '0 -2px 8px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          px: { xs: 1, sm: 3 },
          justifyContent: 'center',
        }}
      >
        {/* Menard Logo - Left */}
        <Box sx={{ width: 180, display: 'flex', alignItems: 'center' }}>
          <Box
            component="img"
            src="/Menard-Vertical.png"
            alt="Menard"
            sx={{ height: 40, filter: isDark ? 'brightness(0) invert(1)' : 'none' }}
          />
        </Box>

        {/* Center Links */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 4 }, flex: 1, justifyContent: 'center' }}>
          <Typography
            variant="body2"
            component="a"
            href="https://menardtechnicalservices.com/feedback"
            sx={footerLinkSx}
          >
            Feedback
          </Typography>
          <Typography
            variant="body2"
            component="a"
            href="https://geotecdev.github.io/menard_technique_gallery/"
            sx={footerLinkSx}
          >
            Discover
          </Typography>
        </Box>

        {/* Right spacer (keeps the links centered; no version on the login page) */}
        <Box sx={{ width: 180 }} />
      </Box>
    </Box>
  );
}

export default LoginPage;
